import React, { useCallback, useContext, useState } from 'react';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import { ChatbotWidgetContext } from 'components/ChatbotWidget/utils/ChatbotWidgetContext';
import { View } from 'components/ChatbotWidget/constants/view';
import { Button } from 'components/baseComponents/Button';
import { Typography, Input, Label, Icon } from 'gd-design-library';
import styles from './CallSignIn.module.scss';
import classNames from 'classnames/bind';
import { useStartCall } from 'components/ChatbotWidget/hooks/useStartCall';

const cx = classNames.bind(styles);

const CN = 'call-sign-in';
const headerClassName = `${CN}__header`;
const backButtonClassName = `${CN}__back-button`;
const checkboxLabelClassName = `${CN}__checkbox-label`;

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

interface Errors {
  name?: string;
  phone?: string;
}

export const CallSignIn = (): React.ReactElement => {
  const [ recordingPermission, setRecordingPermission ] = useState(true);
  const [ errors, setErrors ] = useState<Errors>({});
  const startCall = useStartCall(recordingPermission);
  const [ isCallLoading, setIsCallLoading ] = useState(false);

  const { setView, setClient, options, user } = useContext(ChatbotWidgetContext);
  const { name, setName, phone, setPhone } = user;

  const signIn = useCallback(async () => {
    const currentErrors: Errors = {};

    if (!name.trim()) {
      currentErrors.name = 'Please enter your name';
    } else {
      currentErrors.name = undefined;
    }

    if (!phone || !PHONE_REGEX.test(phone)) {
      currentErrors.phone = 'Please enter a valid phone number';
    } else {
      currentErrors.phone = undefined;
    }

    setErrors(currentErrors);

    if (!currentErrors.phone && !currentErrors.name) {
      setIsCallLoading(true);
      startCall();
    }
  }, [ name, phone, setClient, options ]);

  return (
    <div className={ cx(CN) }>
      <div className={ cx(headerClassName) }>
        <div>
          <Button
            iconStart={ <Icon name='arrowLeft' /> }
            onClick={ (): void => setView(View.SELECT_CONTACT_OPTION) }
            variant='text'
            className={ cx(backButtonClassName) }
          >
            Back
          </Button>
        </div>
        <Typography variant='h4' align='center'>We&apos;ll Call You</Typography>
      </div>
      <Input
        variant='text'
        label='Name'
        name='name'
        autocomplete='name'
        value={ name }
        onChange={ (e): void => setName(e.target.value) }
        color={ errors.name ? 'error' : 'primary' }
        helperText={ errors.name }
      />
      <Input
        variant='tel'
        label='Phone'
        name='phone'
        autocomplete='tel'
        value={ phone }
        onChange={ (e): void => setPhone(e.target.value) }
        color={ errors.phone ? 'error' : 'primary' }
        helperText={ errors.phone }
      />
      <Label className={ cx(checkboxLabelClassName) }>
        <Input
          name='checkbox'
          role='checkbox'
          variant='checkbox'
          checked={ recordingPermission }
          onChange={ (e): void => setRecordingPermission(e.target.checked) }
        />
        Allow recording
      </Label>
      <Button
        variant='primary'
        onClick={ signIn }
        loading={ isCallLoading }
        iconStart={ <PhoneCallbackIcon /> }
      >
        Call Me
      </Button>
    </div>
  );
};
